import { analyzeFoodForAcne, type FoodScanAnalysis } from './foodAcneEngine';
import type { ScanAnalysis } from '../services/scanService';

// Import restaurant menu utilities
// The file exists at src/data/restaurantMenus.ts and has proper exports
// If TypeScript shows an error, it's an IDE cache issue - restart TS Server
import type { RestaurantMenuItem } from '../data/restaurantMenus';
import { getRestaurantMenuItems, getRestaurantCuisine } from '../data/restaurantMenus';
import { restaurantMenuService, fuzzyMatchDish } from '../services/restaurantMenuService';

export interface RestaurantMenuAnalysisResult {
  restaurantName: string;
  menuItemName: string;
  acneImpactScore: number; // 1–100
  acneImpactCategory: 'low' | 'moderate' | 'high';
  explanation: string;
  triggers: string[];
  protectiveFactors: string[];
  isRecommended: boolean; // acne-friendly enough to say "yes"
  sameRestaurantAlternatives: {
    name: string;
    explanation: string;
    acneImpactScore: number;
  }[];
  hasRestaurantMenu: boolean; // true if we have the restaurant's actual menu, false if using generic suggestions
  menuSource: 'exact' | 'template' | 'generic' | 'api'; // 'exact' = exact restaurant match, 'template' = cuisine template, 'generic' = generic suggestions, 'api' = found via internet search
}

export interface AnalyzeRestaurantMenuItemParams {
  restaurantName: string;
  menuItemName: string;
  menuItemDescription?: string;
  optionalMenuContext?: string; // for future API integration
  dietaryPreference?: 'vegetarian' | 'non-vegetarian';
  scanAnalysis?: ScanAnalysis; // User's latest scan analysis for personalized recommendations
  subscriptionTier?: 'free' | 'plus' | 'pro' | 'elite'; // User's subscription tier for feature access
}

/**
 * Analyzes a restaurant menu item for acne impact and suggests same-restaurant alternatives
 * Now uses the restaurant menu service to ensure all alternatives are from the specific restaurant
 */
export async function analyzeRestaurantMenuItem(
  params: AnalyzeRestaurantMenuItemParams
): Promise<RestaurantMenuAnalysisResult> {
  const { restaurantName, menuItemName, menuItemDescription, optionalMenuContext, dietaryPreference, scanAnalysis, subscriptionTier } = params;
  
  // Use existing food acne engine to analyze the menu item
  const combinedText = `${menuItemName} ${menuItemDescription || ''} ${optionalMenuContext || ''}`.trim();
  const foodAnalysis: FoodScanAnalysis = analyzeFoodForAcne({
    foodName: menuItemName,
    ingredientsText: menuItemDescription || optionalMenuContext,
  });

  // Determine if recommended (acne-friendly)
  const isRecommended = foodAnalysis.acneImpactScore <= 40;

  // Generate restaurant-specific explanation
  const explanation = generateRestaurantExplanation(
    foodAnalysis,
    restaurantName,
    menuItemName
  );

  // Get restaurant menu using the service
  const restaurantMenu = await restaurantMenuService.getMenuForRestaurant(restaurantName);

  // Suggest same-restaurant alternatives (personalized based on skin health index)
  // This will ONLY use items from the restaurant's menu if available
  const { alternatives, hasRestaurantMenu, menuSource } = await suggestSameRestaurantAlternatives({
    restaurantName,
    menuItemName,
    currentItemScore: foodAnalysis.acneImpactScore, // Pass current item's score to filter alternatives
    acneImpactCategory: foodAnalysis.acneImpactCategory,
    tags: foodAnalysis.tags,
    cuisine: foodAnalysis.cuisine,
    lowerName: menuItemName.toLowerCase(),
    dietaryPreference,
    scanAnalysis, // Pass scan analysis for personalized recommendations
    restaurantMenu, // Pass the menu if available
    subscriptionTier, // Pass subscription tier for Pro/Elite users to get more alternatives
  });

  return {
    restaurantName,
    menuItemName,
    acneImpactScore: foodAnalysis.acneImpactScore,
    acneImpactCategory: foodAnalysis.acneImpactCategory,
    explanation,
    triggers: foodAnalysis.triggers,
    protectiveFactors: foodAnalysis.protectiveFactors,
    isRecommended,
    sameRestaurantAlternatives: alternatives,
    hasRestaurantMenu,
    menuSource,
  };
}

/**
 * Generates restaurant-specific explanation text
 */
function generateRestaurantExplanation(
  analysis: FoodScanAnalysis,
  restaurantName: string,
  menuItemName: string
): string {
  const { acneImpactScore, acneImpactCategory, triggers, protectiveFactors } = analysis;

  if (acneImpactCategory === 'high') {
    const triggerList = triggers.slice(0, 3).filter(t => t && t.trim().length > 0).join(', ');
    if (triggers.includes('deep-fried') && triggers.includes('high sugar')) {
      return `This item is deep-fried and coated in a sugary glaze, which can spike blood sugar and increase inflammation, making it more likely to trigger acne. At ${restaurantName}, consider choosing a grilled or baked option with less sauce instead.`;
    } else if (triggers.includes('deep-fried')) {
      return `This item is deep-fried, which means it's high in oil and processed fats. That combination can spike insulin and inflammation, which may worsen acne for some people. At ${restaurantName}, look for grilled, baked, or steamed options.`;
    } else if (triggers.includes('high sugar') || triggers.includes('sweet glaze/sauce')) {
      return `This item is high in sugar and refined carbs, which can spike blood sugar and may worsen acne. At ${restaurantName}, consider options with less sauce or naturally sweet ingredients.`;
    } else if (triggers.includes('dairy')) {
      return `This item is dairy-heavy, which can trigger acne for some people. At ${restaurantName}, consider dairy-free or lighter cheese options.`;
    } else if (triggerList && triggerList.length > 0) {
      return `This item contains ${triggerList}, which can spike insulin and may worsen acne for some people. At ${restaurantName}, look for lighter options with more vegetables and less processed ingredients.`;
    } else {
      return `This item may have a high impact on acne for some people. At ${restaurantName}, look for lighter options with more vegetables and less processed ingredients.`;
    }
  } else if (acneImpactCategory === 'moderate') {
    if (triggers.length > 0 && protectiveFactors.length > 0) {
      return `This item has some ${triggers[0]} but also ${protectiveFactors[0]}, so it's okay occasionally in moderation. At ${restaurantName}, you could make it even better by asking for extra vegetables or less sauce.`;
    } else if (triggers.length > 0) {
      return `This item contains ${triggers[0]}, which may have a moderate impact on acne for some individuals. At ${restaurantName}, consider lighter versions or adding more vegetables.`;
    } else {
      return `This item has a moderate impact on acne. At ${restaurantName}, you could make it better by choosing grilled options or adding more vegetables.`;
    }
  } else {
    // Low impact
    if (protectiveFactors.length > 0) {
      return `This is a relatively skin-friendly choice at ${restaurantName}! It contains ${protectiveFactors.join(' and ')}, which supports skin health. If you'd like to upgrade it even more, consider asking for extra vegetables or whole grain options if available.`;
    } else {
      return `This is a relatively skin-friendly choice at ${restaurantName}. It appears to have minimal acne-triggering ingredients.`;
    }
  }
}

/**
 * Infers cuisine from menu item name (as fallback if restaurant name doesn't match)
 */
function inferCuisineFromMenuItem(menuItemName: string): string {
  const lowerMenu = menuItemName.toLowerCase();
  
  // Indian cuisine indicators
  if (lowerMenu.includes('naan') || lowerMenu.includes('dal') || lowerMenu.includes('palak') || 
      lowerMenu.includes('paneer') || lowerMenu.includes('biryani') || lowerMenu.includes('tikka') || 
      lowerMenu.includes('masala') || lowerMenu.includes('sambar') || lowerMenu.includes('curry') && 
      (lowerMenu.includes('chicken') || lowerMenu.includes('lamb') || lowerMenu.includes('goat'))) {
    return 'indian';
  }
  
  // Chinese cuisine indicators
  if (lowerMenu.includes('orange chicken') || lowerMenu.includes('general tso') || 
      lowerMenu.includes('kung pao') || lowerMenu.includes('fried rice') || lowerMenu.includes('lo mein') ||
      lowerMenu.includes('sweet and sour') || lowerMenu.includes('mapo') || lowerMenu.includes('peking')) {
    return 'chinese';
  }
  
  // Mexican cuisine indicators
  if (lowerMenu.includes('burrito') || lowerMenu.includes('taco') || lowerMenu.includes('quesadilla') ||
      lowerMenu.includes('enchilada') || lowerMenu.includes('fajita') || lowerMenu.includes('chimichanga')) {
    return 'mexican';
  }
  
  // Italian cuisine indicators
  if (lowerMenu.includes('pasta') || lowerMenu.includes('pizza') || lowerMenu.includes('alfredo') ||
      lowerMenu.includes('carbonara') || lowerMenu.includes('risotto') || lowerMenu.includes('lasagna')) {
    return 'italian';
  }
  
  // Japanese cuisine indicators
  if (lowerMenu.includes('sushi') || lowerMenu.includes('ramen') || lowerMenu.includes('teriyaki') ||
      lowerMenu.includes('sashimi') || lowerMenu.includes('tempura') || lowerMenu.includes('udon')) {
    return 'japanese';
  }
  
  // Thai cuisine indicators
  if (lowerMenu.includes('pad thai') || lowerMenu.includes('green curry') || lowerMenu.includes('tom yum') ||
      lowerMenu.includes('massaman') || lowerMenu.includes('som tam')) {
    return 'thai';
  }
  
  return 'generic';
}

/**
 * Detects restaurant cuisine/type from restaurant name
 */
function detectRestaurantCuisine(restaurantName: string, menuItemName: string): string {
  const lowerRestaurant = restaurantName.toLowerCase();
  const lowerMenu = menuItemName.toLowerCase();
  
  // Indian cuisine
  if (lowerRestaurant.includes('indian') || lowerRestaurant.includes('tandoor') || lowerRestaurant.includes('curry') || 
      lowerRestaurant.includes('thanal') || lowerRestaurant.includes('bombay') || lowerRestaurant.includes('delhi') ||
      lowerMenu.includes('naan') || lowerMenu.includes('dal') || lowerMenu.includes('palak') || lowerMenu.includes('paneer') ||
      lowerMenu.includes('biryani') || lowerMenu.includes('tikka') || lowerMenu.includes('masala') || lowerMenu.includes('sambar')) {
    return 'indian';
  }
  
  // Chinese cuisine
  if (lowerRestaurant.includes('chinese') || lowerRestaurant.includes('panda') || lowerRestaurant.includes('p.f. chang') ||
      lowerRestaurant.includes('peking') || lowerMenu.includes('orange chicken') || lowerMenu.includes('general tso') ||
      lowerMenu.includes('kung pao') || lowerMenu.includes('fried rice') || lowerMenu.includes('lo mein')) {
    return 'chinese';
  }
  
  // Mexican cuisine
  if (lowerRestaurant.includes('mexican') || lowerRestaurant.includes('chipotle') || lowerRestaurant.includes('qdoba') ||
      lowerRestaurant.includes('taco') || lowerRestaurant.includes('burrito') || lowerMenu.includes('burrito') ||
      lowerMenu.includes('taco') || lowerMenu.includes('quesadilla') || lowerMenu.includes('enchilada')) {
    return 'mexican';
  }
  
  // Italian cuisine
  if (lowerRestaurant.includes('italian') || lowerRestaurant.includes('olive garden') || lowerRestaurant.includes('carrabba') ||
      lowerMenu.includes('pasta') || lowerMenu.includes('pizza') || lowerMenu.includes('alfredo') || lowerMenu.includes('carbonara')) {
    return 'italian';
  }
  
  // Japanese cuisine
  if (lowerRestaurant.includes('japanese') || lowerRestaurant.includes('sushi') || lowerRestaurant.includes('teriyaki') ||
      lowerMenu.includes('sushi') || lowerMenu.includes('ramen') || lowerMenu.includes('teriyaki')) {
    return 'japanese';
  }
  
  // Thai cuisine
  if (lowerRestaurant.includes('thai') || lowerMenu.includes('pad thai') || lowerMenu.includes('curry') && lowerMenu.includes('green')) {
    return 'thai';
  }
  
  // Fast food
  if (lowerRestaurant.includes('mcdonald') || lowerRestaurant.includes('burger king') || lowerRestaurant.includes('wendy') ||
      lowerRestaurant.includes('subway') || lowerRestaurant.includes('kfc') || lowerRestaurant.includes('taco bell') ||
      lowerRestaurant.includes('dunkin') || lowerRestaurant.includes('dunkin donuts')) {
    return 'american_fast_food';
  }
  
  // American casual
  if (lowerRestaurant.includes('cheesecake factory') || lowerRestaurant.includes('applebees') || lowerRestaurant.includes('chili') ||
      lowerRestaurant.includes('outback') || lowerRestaurant.includes('red lobster') || lowerRestaurant.includes('cheesecake')) {
    return 'american_casual';
  }
  
  // Return generic if no cuisine detected
  return 'generic';
}

/**
 * Seeded shuffle function for consistent selection based on skin health index
 */
function seededShuffle<T>(array: T[], seed: number): T[] {
  const shuffled = [...array];
  let currentSeed = seed;
  for (let i = shuffled.length - 1; i > 0; i--) {
    currentSeed = (currentSeed * 9301 + 49297) % 233280;
    const j = Math.floor((currentSeed / 233280) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Suggests better alternatives from the same restaurant
 * CRITICAL: Only returns items from the restaurant's actual menu
 * If no menu is available, returns empty array (no hallucinated dishes)
 */
async function suggestSameRestaurantAlternatives(params: {
  restaurantName: string;
  menuItemName: string;
  currentItemScore: number; // Current menu item's acne impact score
  acneImpactCategory: 'low' | 'moderate' | 'high';
  tags: string[];
  cuisine?: string;
  lowerName: string;
  dietaryPreference?: 'vegetarian' | 'non-vegetarian';
  scanAnalysis?: ScanAnalysis; // User's latest scan analysis
  restaurantMenu?: { restaurantName: string; items: RestaurantMenuItem[]; isExactMatch: boolean; source: 'database' | 'template' | 'api' } | null; // Restaurant menu if already fetched
  subscriptionTier?: 'free' | 'plus' | 'pro' | 'elite'; // User's subscription tier
}): Promise<{
  alternatives: RestaurantMenuAnalysisResult['sameRestaurantAlternatives'];
  hasRestaurantMenu: boolean;
  menuSource: 'exact' | 'template' | 'generic' | 'api';
}> {
  const { restaurantName, menuItemName, currentItemScore, acneImpactCategory, tags, cuisine, lowerName, dietaryPreference, scanAnalysis, restaurantMenu, subscriptionTier } = params;
  
  // Get user's skin health index for personalized recommendations
  const glowScore = scanAnalysis?.skinHealthIndex || 70;
  const estimatedPH = scanAnalysis?.acneAnalysis?.estimatedPH;
  const acneSeverity = scanAnalysis?.acneAnalysis?.overallSeverity || 'mild';
  
  // Determine maximum acne impact score for recommendations based on user's skin health
  // Users with worse skin (lower glowScore) get stricter recommendations (lower max scores)
  let maxAllowedScore: number;
  if (glowScore >= 0 && glowScore <= 25) {
    maxAllowedScore = 25; // Very strict for users with poor skin health
  } else if (glowScore >= 26 && glowScore <= 50) {
    maxAllowedScore = 50; // Moderate strictness
  } else if (glowScore >= 51 && glowScore <= 75) {
    maxAllowedScore = 75; // More lenient
  } else {
    maxAllowedScore = 100; // Most lenient for users with excellent skin health
  }
  
  console.log('[Restaurant Analyzer] Personalized score filtering:', {
    userGlowScore: glowScore,
    maxAllowedScore,
    scoreRange: `0-${maxAllowedScore}`
  });
  
  // Create seed for consistent selection based on user's skin profile
  const seed = Math.floor(glowScore * 1000 + (estimatedPH || 5) * 100 + (acneSeverity === 'mild' ? 1 : acneSeverity === 'moderate' ? 2 : 3) * 10);
  
  // Get user's specific skin/acne profile for accurate filtering
  // Check if user has inflammation based on regions or severity
  const hasInflammation = scanAnalysis?.acneAnalysis?.regions?.some((region: any) => 
    region.severity === 'moderate' || region.severity === 'severe' || region.severity === 'mild_to_moderate'
  ) || acneSeverity === 'moderate' || acneSeverity === 'severe' || acneSeverity === 'mild_to_moderate';
  
  // Get restaurant menu if not already provided
  const menu = restaurantMenu || await restaurantMenuService.getMenuForRestaurant(restaurantName);
  
  // Debug logging for Pro/Elite users
  if (subscriptionTier === 'pro' || subscriptionTier === 'elite') {
    console.log('[Restaurant Analyzer] Pro/Elite user - Menu lookup:', {
      restaurantName,
      menuFound: !!menu,
      menuItemsCount: menu?.items?.length || 0,
      subscriptionTier,
    });
  }
  
  // CRITICAL: If no menu is available, return empty array (no hallucinated dishes)
  if (!menu || !menu.items || menu.items.length === 0) {
    return {
      alternatives: [],
      hasRestaurantMenu: false,
      menuSource: 'generic',
    };
  }
  
  // We have a menu - use only items from this restaurant's menu
  const menuItems = menu.items;
  const isExactMatch = menu.isExactMatch;
  const menuSource = isExactMatch ? 'exact' : (menu.source === 'template' ? 'template' : menu.source === 'api' ? 'api' : 'generic');
  
  console.log('[Restaurant Analyzer] Menu found:', {
    restaurantName,
    totalMenuItems: menuItems.length,
    menuSource,
    isExactMatch
  });
  
  // Filter out the current menu item (don't suggest the same item)
  const otherMenuItems = menuItems.filter(item => {
    const itemNameLower = item.name.toLowerCase().trim();
    const currentItemLower = menuItemName.toLowerCase().trim();
    // Exclude if it's the same item (exact or partial match)
    return !itemNameLower.includes(currentItemLower) && !currentItemLower.includes(itemNameLower);
  });
  
  console.log('[Restaurant Analyzer] After filtering current item:', {
    otherMenuItemsCount: otherMenuItems.length,
    currentItemName: menuItemName
  });
  
  if (otherMenuItems.length === 0) {
    // If no other items, return empty (this shouldn't happen often)
      console.log('[Restaurant Analyzer] No other menu items found after filtering current item');
    return {
      alternatives: [],
      hasRestaurantMenu: true,
      menuSource,
    };
  }
  
  // Always show at least 5-6 alternatives for all users
  // Pro/Elite users get more alternatives
  const isProOrElite = subscriptionTier === 'pro' || subscriptionTier === 'elite';
  const maxAlternatives = acneImpactCategory === 'low' 
    ? (isProOrElite ? 8 : 6)  // Increased from 2-3 to 6-8
    : (isProOrElite ? 10 : 8); // Increased from 6-8 to 8-10
  
  // Analyze each menu item to get accurate acne impact score
  const analyzedItems = otherMenuItems
      .map((item: RestaurantMenuItem) => {
        // Re-analyze each item to get accurate score based on actual ingredients
        const itemAnalysis = analyzeFoodForAcne({
          foodName: item.name,
          ingredientsText: item.explanation || item.name,
        });
        
        return {
          ...item,
          actualAcneImpactScore: itemAnalysis.acneImpactScore,
          actualTriggers: itemAnalysis.triggers,
          actualTags: itemAnalysis.tags,
        };
      });
  
  // CRITICAL: Only show alternatives with LOWER scores than the current item
  // Lower score = better for skin (less acne impact)
  // For Pro/Elite: Only show items with score ≤ 35 (premium skin-friendly options)
  // For other tiers: Only show alternatives with strictly lower scores than current item
  let betterItems: any[] = [];
  
  // Filter items based on user's personalized score range
  // All items must be within the user's allowed score range AND better than or equal to current item
    betterItems = analyzedItems.filter((item: any) => {
    return item.actualAcneImpactScore <= maxAllowedScore && item.actualAcneImpactScore <= currentItemScore;
  });
  
  console.log('[Restaurant Analyzer] Personalized filtering:', {
    totalItems: analyzedItems.length,
    filteredItems: betterItems.length,
    maxAllowedScore,
    currentItemScore,
    userGlowScore: glowScore
  });
  
  // If we don't have enough items, be slightly more lenient (but still within user's range)
  if (betterItems.length < 6) {
    // Allow items up to 5 points higher than current item, but still within maxAllowedScore
    const relaxedThreshold = Math.min(currentItemScore + 5, maxAllowedScore);
    const relaxedItems = analyzedItems.filter((item: any) => {
      return item.actualAcneImpactScore <= relaxedThreshold && item.actualAcneImpactScore <= maxAllowedScore;
    });
    // Prefer the better items, but include relaxed items if needed
    const additionalItems = relaxedItems.filter(item => 
      !betterItems.some(better => better.name.toLowerCase() === item.name.toLowerCase())
    );
    betterItems = [...betterItems, ...additionalItems];
    console.log('[Restaurant Analyzer] Added relaxed items within user range:', {
      added: additionalItems.length,
      total: betterItems.length,
      relaxedThreshold
    });
  }
    
    // Filter by dietary preference
    // Vegetarian: Only show vegetarian options (no meat/seafood)
    // Non-vegetarian: Show both vegetarian and non-vegetarian options (no filtering)
    // Comprehensive meat/seafood keywords list
    const meatKeywords = ['chicken', 'beef', 'pork', 'lamb', 'turkey', 'fish', 'salmon', 'tuna', 'shrimp', 'meat', 'burger', 'steak', 'wings', 'ribs', 'bacon', 'sausage', 'ham', 'duck', 'seafood', 'crab', 'lobster', 'mutton', 'veal', 'bison', 'venison', 'cod', 'halibut', 'mackerel', 'sardine', 'anchovy', 'octopus', 'squid', 'calamari', 'scallop', 'mussel', 'oyster', 'clam', 'prawn', 'crayfish', 'eel', 'tilapia', 'catfish', 'trout', 'bass', 'perch', 'snapper', 'grouper', 'mahi', 'swordfish', 'marlin', 'patty', 'patties', 'sirloin', 'ribeye', 'filet', 'tenderloin', 'brisket', 'chop', 'cutlet', 'meatball', 'meatballs', 'chorizo', 'pepperoni', 'prosciutto', 'pancetta', 'pastrami', 'corned', 'jerky', 'bologna', 'salami', 'bratwurst', 'kielbasa', 'andouille', 'gyro', 'kebab', 'kabob', 'satay', 'teriyaki chicken', 'chicken teriyaki'];
    
    // Helper function to check if item contains meat
    const containsMeat = (item: any): boolean => {
        const itemNameLower = item.name.toLowerCase();
        const explanationLower = (item.explanation || '').toLowerCase();
      const combinedText = `${itemNameLower} ${explanationLower}`;
      return meatKeywords.some(keyword => combinedText.includes(keyword));
    };
    
    if (dietaryPreference === 'vegetarian') {
      const beforeFilter = betterItems.length;
      betterItems = betterItems.filter((item: any) => !containsMeat(item));
      
      console.log('[Restaurant Analyzer] Vegetarian filtering:', {
        beforeFilter,
        afterFilter: betterItems.length,
        removed: beforeFilter - betterItems.length,
        dietaryPreference,
        removedItems: analyzedItems.filter(item => containsMeat(item)).map(item => item.name)
      });
      
      // If vegetarian filtering removed too many items, try to add back some vegetarian-friendly items
      if (betterItems.length < 6 && analyzedItems.length > betterItems.length) {
        const vegetarianItems = analyzedItems.filter((item: any) => {
          return !containsMeat(item) && 
                 item.actualAcneImpactScore <= maxAllowedScore && 
                 item.actualAcneImpactScore <= currentItemScore;
        });
        
        const additionalVegetarian = vegetarianItems
          .filter(item => !betterItems.some(better => better.name.toLowerCase() === item.name.toLowerCase()))
          .slice(0, 6 - betterItems.length);
        
        if (additionalVegetarian.length > 0) {
          betterItems = [...betterItems, ...additionalVegetarian];
          console.log('[Restaurant Analyzer] Added vegetarian items to reach minimum:', {
            added: additionalVegetarian.length,
            total: betterItems.length,
            addedItems: additionalVegetarian.map(item => item.name)
          });
        }
      }
    } else {
      // Non-vegetarian OR undefined: Show both vegetarian and non-vegetarian options (no filtering needed)
      // This ensures non-vegetarian users see all available options
      const meatItemsCount = betterItems.filter((item: any) => containsMeat(item)).length;
      const vegetarianItemsCount = betterItems.length - meatItemsCount;
      
      console.log('[Restaurant Analyzer] Non-vegetarian user - showing all options (vegetarian + non-vegetarian):', {
        totalItems: betterItems.length,
        vegetarianItems: vegetarianItemsCount,
        nonVegetarianItems: meatItemsCount,
        dietaryPreference: dietaryPreference || 'not specified (defaulting to non-vegetarian)'
      });
    }
    
    // Filter out items with triggers that are problematic for this specific user
    // Made less strict - only filter if item has MULTIPLE problematic triggers
    // For Pro/Elite, apply less strict filtering to ensure alternatives are shown
    let filteredByTriggers = betterItems.filter((item: any) => {
      const triggerCount = item.actualTriggers.length;
      let problematicCount = 0;
      
      // If user has inflammation, count inflammatory triggers
      if (hasInflammation) {
        const inflammatoryTriggers = ['deep-fried', 'processed', 'high sugar', 'refined carbs', 'dairy'];
        problematicCount += item.actualTriggers.filter((trigger: string) => 
          inflammatoryTriggers.some(inflam => trigger.toLowerCase().includes(inflam))
        ).length;
      }
      
      // If user has high pH, count pH-raising triggers
      if (estimatedPH && estimatedPH > 5.5) {
        const phRaisingTriggers = ['dairy', 'processed', 'high sugar'];
        problematicCount += item.actualTriggers.filter((trigger: string) => 
          phRaisingTriggers.some(ph => trigger.toLowerCase().includes(ph))
        ).length;
      }
      
      // If user has severe acne, count problematic triggers
      if (acneSeverity === 'severe' || acneSeverity === 'mild_to_moderate') {
        const problematicTriggers = ['dairy', 'high sugar', 'deep-fried', 'processed'];
        problematicCount += item.actualTriggers.filter((trigger: string) => 
          problematicTriggers.some(prob => trigger.toLowerCase().includes(prob))
        ).length;
      }
      
      // Only filter out if item has 3+ problematic triggers (very lenient filtering)
      // This allows more options through while still avoiding the worst items
      // Pro/Elite users get even more lenient filtering (allow 5+ triggers instead of 3+)
      // This ensures they always get alternatives when a menu exists
      const maxProblematicTriggers = isProOrElite ? 5 : 3; // Increased from 4:2 to 5:3
      return problematicCount < maxProblematicTriggers;
    });
    
    console.log('[Restaurant Analyzer] After trigger filtering:', {
      beforeFilter: betterItems.length,
      afterFilter: filteredByTriggers.length,
      removed: betterItems.length - filteredByTriggers.length
    });
    
    // If trigger filtering removed too many items, be more lenient
    // This ensures alternatives are always shown when menu exists
    if (filteredByTriggers.length < 6 && betterItems.length > filteredByTriggers.length) {
      // If we have less than 6 items after filtering, include some of the filtered items back
      const additionalItems = betterItems
        .filter(item => !filteredByTriggers.some(filtered => filtered.name === item.name))
        .slice(0, 6 - filteredByTriggers.length);
      filteredByTriggers = [...filteredByTriggers, ...additionalItems];
      console.log('[Restaurant Analyzer] Added back filtered items to reach minimum:', {
        added: additionalItems.length,
        total: filteredByTriggers.length
      });
    }
    
    betterItems = filteredByTriggers;
    
    // Score-based selection: For Pro/Elite, ensure all items are ≤ 35 (already filtered above)
    // For other tiers, apply score threshold based on skin health
    let itemsToUse = betterItems;
    
    // Apply personalized score filtering based on user's skin health
    // Items are already filtered to maxAllowedScore, but we can further refine if needed
    // For all users, use the personalized maxAllowedScore (already applied above)
    // No additional threshold filtering needed since we're already using personalized ranges
    itemsToUse = betterItems;
    
    console.log('[Restaurant Analyzer] Final items after personalized filtering:', {
      count: itemsToUse.length,
      maxAllowedScore,
      userGlowScore: glowScore,
      scoreRange: `0-${maxAllowedScore}`
    });
    // Pro/Elite: itemsToUse already contains only items with score ≤ 35
    
    // Sort by best score first (using actual analyzed score)
    const sortedItems = itemsToUse
      .sort((a: any, b: any) => a.actualAcneImpactScore - b.actualAcneImpactScore);
    
    // Shuffle based on user's skin health index to ensure different users get different recommendations
    const shuffledItems = seededShuffle(sortedItems, seed);
    
    // Take top items after shuffling
    // Ensure we always return at least 6 items if available
    const minItems = 6; // Always try to return at least 6 items
    const maxToTake = Math.max(minItems, maxAlternatives);
    // Take as many as available, but at least 6 if possible
    let selectedItems = shuffledItems.slice(0, Math.max(minItems, Math.min(maxToTake, shuffledItems.length)));
    
    console.log('[Restaurant Analyzer] Selected items after filtering:', {
      selectedCount: selectedItems.length,
      availableCount: shuffledItems.length,
      minItems,
      maxAlternatives,
      maxToTake,
      finalCount: selectedItems.length
    });
    
    // FINAL SAFETY CHECK: If we still have less than 6 items, take more from the original list
    // But only items within the user's personalized score range AND dietary preference
    if (selectedItems.length < 6 && analyzedItems.length > selectedItems.length) {
      // Reuse the containsMeat function from above scope
      const meatKeywords = ['chicken', 'beef', 'pork', 'lamb', 'turkey', 'fish', 'salmon', 'tuna', 'shrimp', 'meat', 'burger', 'steak', 'wings', 'ribs', 'bacon', 'sausage', 'ham', 'duck', 'seafood', 'crab', 'lobster', 'mutton', 'veal', 'bison', 'venison', 'cod', 'halibut', 'mackerel', 'sardine', 'anchovy', 'octopus', 'squid', 'calamari', 'scallop', 'mussel', 'oyster', 'clam', 'prawn', 'crayfish', 'eel', 'tilapia', 'catfish', 'trout', 'bass', 'perch', 'snapper', 'grouper', 'mahi', 'swordfish', 'marlin', 'patty', 'patties', 'sirloin', 'ribeye', 'filet', 'tenderloin', 'brisket', 'chop', 'cutlet', 'meatball', 'meatballs', 'chorizo', 'pepperoni', 'prosciutto', 'pancetta', 'pastrami', 'corned', 'jerky', 'bologna', 'salami', 'bratwurst', 'kielbasa', 'andouille', 'gyro', 'kebab', 'kabob', 'satay', 'teriyaki chicken', 'chicken teriyaki'];
      const containsMeatCheck = (item: any): boolean => {
        const itemNameLower = item.name.toLowerCase();
        const explanationLower = (item.explanation || '').toLowerCase();
        const combinedText = `${itemNameLower} ${explanationLower}`;
        return meatKeywords.some(keyword => combinedText.includes(keyword));
      };
      
      const remainingItems = analyzedItems
        .filter(item => {
          const notAlreadySelected = !selectedItems.some(selected => selected.name.toLowerCase() === item.name.toLowerCase());
          const withinScoreRange = item.actualAcneImpactScore <= maxAllowedScore;
          const meetsDietaryPreference = dietaryPreference === 'vegetarian' ? !containsMeatCheck(item) : true;
          return notAlreadySelected && withinScoreRange && meetsDietaryPreference;
        })
        .sort((a: any, b: any) => a.actualAcneImpactScore - b.actualAcneImpactScore)
        .slice(0, 6 - selectedItems.length);
      
      if (remainingItems.length > 0) {
        selectedItems = [...selectedItems, ...remainingItems];
        console.log('[Restaurant Analyzer] Added remaining items within user range and dietary preference to reach 6:', {
          added: remainingItems.length,
          total: selectedItems.length,
          maxAllowedScore,
          userGlowScore: glowScore,
          dietaryPreference,
          addedItems: remainingItems.map(item => item.name)
        });
      }
    }
    
  // FINAL VERIFICATION: Double-check that all items meet dietary preference (especially for vegetarian users)
  // NOTE: For non-vegetarian users, we intentionally do NOT filter - they should see both vegetarian and non-vegetarian options
  if (dietaryPreference === 'vegetarian') {
    const meatKeywords = ['chicken', 'beef', 'pork', 'lamb', 'turkey', 'fish', 'salmon', 'tuna', 'shrimp', 'meat', 'burger', 'steak', 'wings', 'ribs', 'bacon', 'sausage', 'ham', 'duck', 'seafood', 'crab', 'lobster', 'mutton', 'veal', 'bison', 'venison', 'cod', 'halibut', 'mackerel', 'sardine', 'anchovy', 'octopus', 'squid', 'calamari', 'scallop', 'mussel', 'oyster', 'clam', 'prawn', 'crayfish', 'eel', 'tilapia', 'catfish', 'trout', 'bass', 'perch', 'snapper', 'grouper', 'mahi', 'swordfish', 'marlin', 'patty', 'patties', 'sirloin', 'ribeye', 'filet', 'tenderloin', 'brisket', 'chop', 'cutlet', 'meatball', 'meatballs', 'chorizo', 'pepperoni', 'prosciutto', 'pancetta', 'pastrami', 'corned', 'jerky', 'bologna', 'salami', 'bratwurst', 'kielbasa', 'andouille', 'gyro', 'kebab', 'kabob', 'satay', 'teriyaki chicken', 'chicken teriyaki'];
    const containsMeatFinal = (item: any): boolean => {
      const itemNameLower = item.name.toLowerCase();
      const explanationLower = (item.explanation || '').toLowerCase();
      const combinedText = `${itemNameLower} ${explanationLower}`;
      return meatKeywords.some(keyword => combinedText.includes(keyword));
    };
    
    const beforeFinalCheck = selectedItems.length;
    selectedItems = selectedItems.filter((item: any) => !containsMeatFinal(item));
    
    if (beforeFinalCheck !== selectedItems.length) {
      console.warn('[Restaurant Analyzer] FINAL CHECK: Removed non-vegetarian items that slipped through:', {
        before: beforeFinalCheck,
        after: selectedItems.length,
        removed: beforeFinalCheck - selectedItems.length
      });
    }
    
    console.log('[Restaurant Analyzer] Final vegetarian verification:', {
      totalItems: selectedItems.length,
      allVegetarian: selectedItems.every(item => !containsMeatFinal(item)),
      itemNames: selectedItems.map(item => item.name)
    });
  } else {
    // For non-vegetarian users, verify we have both types of items
    const meatKeywords = ['chicken', 'beef', 'pork', 'lamb', 'turkey', 'fish', 'salmon', 'tuna', 'shrimp', 'meat', 'burger', 'steak', 'wings', 'ribs', 'bacon', 'sausage', 'ham', 'duck', 'seafood', 'crab', 'lobster', 'mutton', 'veal', 'bison', 'venison', 'cod', 'halibut', 'mackerel', 'sardine', 'anchovy', 'octopus', 'squid', 'calamari', 'scallop', 'mussel', 'oyster', 'clam', 'prawn', 'crayfish', 'eel', 'tilapia', 'catfish', 'trout', 'bass', 'perch', 'snapper', 'grouper', 'mahi', 'swordfish', 'marlin', 'patty', 'patties', 'sirloin', 'ribeye', 'filet', 'tenderloin', 'brisket', 'chop', 'cutlet', 'meatball', 'meatballs', 'chorizo', 'pepperoni', 'prosciutto', 'pancetta', 'pastrami', 'corned', 'jerky', 'bologna', 'salami', 'bratwurst', 'kielbasa', 'andouille', 'gyro', 'kebab', 'kabob', 'satay', 'teriyaki chicken', 'chicken teriyaki'];
    const containsMeatCheck = (item: any): boolean => {
      const itemNameLower = item.name.toLowerCase();
      const explanationLower = (item.explanation || '').toLowerCase();
      const combinedText = `${itemNameLower} ${explanationLower}`;
      return meatKeywords.some(keyword => combinedText.includes(keyword));
    };
    
    const meatItems = selectedItems.filter((item: any) => containsMeatCheck(item));
    const vegetarianItems = selectedItems.filter((item: any) => !containsMeatCheck(item));
    
    console.log('[Restaurant Analyzer] Final non-vegetarian verification - showing both types:', {
      totalItems: selectedItems.length,
      vegetarianItems: vegetarianItems.length,
      nonVegetarianItems: meatItems.length,
      dietaryPreference: dietaryPreference || 'not specified (defaulting to show all)',
      vegetarianItemNames: vegetarianItems.map(item => item.name),
      nonVegetarianItemNames: meatItems.map(item => item.name)
    });
  }
    
  // Format alternatives based on menu source
  // Ensure we always return at least some alternatives for Pro/Elite when menu exists
  let alternatives = selectedItems.map((item: any) => {
    if (isExactMatch) {
      // Exact match - use exact menu item name (100% accurate)
      return {
        name: item.name,
        explanation: item.explanation || `Skin-friendly option with low acne impact (score: ${item.actualAcneImpactScore})`,
        acneImpactScore: item.actualAcneImpactScore,
      };
    } else {
      // Template match - format as suggestion to indicate it may not be exact
      return {
        name: item.name, // Keep the name but add note in explanation
        explanation: (item.explanation || `Skin-friendly option with low acne impact (score: ${item.actualAcneImpactScore})`) + 
          ` Ask your server if this item or a similar option is available.`,
        acneImpactScore: item.actualAcneImpactScore,
      };
    }
  });
  
  // FALLBACK: If we don't have at least 6 alternatives and we have a menu, use fallback
  if (alternatives.length < 6 && otherMenuItems.length > 0) {
    console.log('[Restaurant Analyzer] Not enough alternatives after filtering, using fallback', {
      alternativesCount: alternatives.length,
      otherMenuItemsCount: otherMenuItems.length,
      currentItemScore,
      subscriptionTier,
      targetCount: 6
    });
    
    // Use ALL other menu items, analyze them, filter to user's personalized score range
    let fallbackItems = otherMenuItems
      .map((item: RestaurantMenuItem) => {
        const itemAnalysis = analyzeFoodForAcne({
          foodName: item.name,
          ingredientsText: item.explanation || item.name,
        });
        return {
          ...item,
          actualAcneImpactScore: itemAnalysis.acneImpactScore,
        };
      })
      .filter((item: any) => item.actualAcneImpactScore <= maxAllowedScore) // Use personalized max score
      .sort((a: any, b: any) => a.actualAcneImpactScore - b.actualAcneImpactScore); // Sort by best (lowest) score first
    
    // Apply dietary preference filtering to fallback items
    if (dietaryPreference === 'vegetarian') {
      const meatKeywords = ['chicken', 'beef', 'pork', 'lamb', 'turkey', 'fish', 'salmon', 'tuna', 'shrimp', 'meat', 'burger', 'steak', 'wings', 'ribs', 'bacon', 'sausage', 'ham', 'duck', 'seafood', 'crab', 'lobster', 'mutton', 'veal', 'bison', 'venison', 'cod', 'halibut', 'mackerel', 'sardine', 'anchovy', 'octopus', 'squid', 'calamari', 'scallop', 'mussel', 'oyster', 'clam', 'prawn', 'crayfish', 'eel', 'tilapia', 'catfish', 'trout', 'bass', 'perch', 'snapper', 'grouper', 'mahi', 'swordfish', 'marlin', 'patty', 'patties', 'sirloin', 'ribeye', 'filet', 'tenderloin', 'brisket', 'chop', 'cutlet', 'meatball', 'meatballs', 'chorizo', 'pepperoni', 'prosciutto', 'pancetta', 'pastrami', 'corned', 'jerky', 'bologna', 'salami', 'bratwurst', 'kielbasa', 'andouille', 'gyro', 'kebab', 'kabob', 'satay', 'teriyaki chicken', 'chicken teriyaki'];
      const containsMeatCheck = (item: any): boolean => {
        const itemNameLower = item.name.toLowerCase();
        const explanationLower = (item.explanation || '').toLowerCase();
        const combinedText = `${itemNameLower} ${explanationLower}`;
        return meatKeywords.some(keyword => combinedText.includes(keyword));
      };
      
      const beforeDietaryFilter = fallbackItems.length;
      fallbackItems = fallbackItems.filter((item: any) => !containsMeatCheck(item));
      console.log('[Restaurant Analyzer] Fallback - Vegetarian filtering:', {
        beforeFilter: beforeDietaryFilter,
        afterFilter: fallbackItems.length,
        removedItems: otherMenuItems
          .map((item: RestaurantMenuItem) => {
            const itemAnalysis = analyzeFoodForAcne({
              foodName: item.name,
              ingredientsText: item.explanation || item.name,
            });
            return { ...item, actualAcneImpactScore: itemAnalysis.acneImpactScore };
          })
          .filter((item: any) => containsMeatCheck(item) && item.actualAcneImpactScore <= maxAllowedScore)
          .map(item => item.name)
      });
    }
    
    fallbackItems = fallbackItems.slice(0, 10); // Take up to 10 items for fallback
    
    console.log('[Restaurant Analyzer] Fallback items filtered by personalized range and dietary preference:', {
      totalAnalyzed: otherMenuItems.length,
      withinRange: fallbackItems.length,
      maxAllowedScore,
      userGlowScore: glowScore,
      dietaryPreference
    });
    
    // Add fallback items to reach at least 6 total
    const neededCount = 6 - alternatives.length;
    const additionalItems = fallbackItems
      .filter((item: any) => !alternatives.some(alt => alt.name.toLowerCase() === item.name.toLowerCase()))
      .slice(0, neededCount); // Fill up to 6 total
    
    if (additionalItems.length > 0) {
      alternatives = [
        ...alternatives,
        ...additionalItems.map((item: any) => ({
      name: item.name,
          explanation: item.explanation || `Skin-friendly option from ${restaurantName} (score: ${item.actualAcneImpactScore})`,
      acneImpactScore: item.actualAcneImpactScore,
        }))
      ];
    }
    
    console.log('[Restaurant Analyzer] Fallback alternatives added. Total:', alternatives.length);
  }
  
  return {
    alternatives,
    hasRestaurantMenu: true,
    menuSource,
  };
}

/**
 * NOTE: All code below this point is legacy/unreachable code that has been
 * replaced by the menu service approach. It is kept for reference only.
 * The suggestSameRestaurantAlternatives function now ONLY returns items
 * from the restaurant's actual menu.
 */

/**
 * NOTE: The code between here and the actual getIndianRestaurantAlternatives function
 * below is unreachable legacy code that should be removed. It's kept temporarily
 * for reference but causes compilation errors.
 */

/**
 * Detects the protein type from menu item name
 */
function detectProteinType(lowerName: string): 'chicken' | 'beef' | 'pork' | 'fish' | 'seafood' | 'meat' | 'none' {
  if (lowerName.includes('chicken')) return 'chicken';
  if (lowerName.includes('beef')) return 'beef';
  if (lowerName.includes('pork')) return 'pork';
  if (lowerName.includes('fish') || lowerName.includes('salmon') || lowerName.includes('tuna') || lowerName.includes('cod') || lowerName.includes('shrimp') || lowerName.includes('seafood')) return 'fish';
  if (lowerName.includes('meat') || lowerName.includes('protein')) return 'meat';
  return 'none';
}

/**
 * Get Indian restaurant specific menu items
 */
function getIndianRestaurantAlternatives(
  restaurantName: string,
  menuItemName: string,
  tags: string[],
  lowerName: string,
  acneImpactCategory: 'low' | 'moderate' | 'high',
  glowScore: number = 70
): RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] {
  const alternatives: RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] = [];
  
  // If it's heavy paneer curry (like chili paneer, palak paneer, etc.)
  if (lowerName.includes('paneer') && (tags.includes('dairy') || tags.includes('deepFried'))) {
    alternatives.push(
      {
        name: 'Tandoori Chicken',
        explanation: `Grilled chicken marinated in spices. Much lower in dairy and oil than paneer dishes.`,
        acneImpactScore: 38,
      },
      {
        name: 'Dal Tadka',
        explanation: `Lentil curry with minimal oil. High in protein and fiber, lower in dairy than paneer dishes.`,
        acneImpactScore: 35,
      },
      {
        name: 'Chana Masala',
        explanation: `Chickpea curry with tomatoes and spices. High in fiber, lower in dairy and oil.`,
        acneImpactScore: 40,
      },
      {
        name: 'Bhindi Masala',
        explanation: `Okra curry with minimal oil. High in fiber, no dairy, better for skin health.`,
        acneImpactScore: 32,
      },
      {
        name: 'Aloo Gobi',
        explanation: `Potato and cauliflower curry with minimal oil. High in fiber, lower in dairy than creamy dishes.`,
        acneImpactScore: 38,
      },
      {
        name: 'Baingan Bharta',
        explanation: `Roasted eggplant curry. High in fiber, minimal oil, no dairy, excellent for skin health.`,
        acneImpactScore: 35,
      },
      {
        name: 'Rajma',
        explanation: `Kidney bean curry with tomatoes and spices. High in protein and fiber, lower in dairy and oil.`,
        acneImpactScore: 38,
      }
    );
  }
  
  // If it's naan + heavy curry, suggest lighter Indian dishes
  else if (lowerName.includes('naan') || lowerName.includes('roti') || lowerName.includes('paratha')) {
    alternatives.push(
      {
        name: 'Dal Tadka',
        explanation: `Lentil curry with minimal oil. High in protein and fiber, lower in dairy than creamy dishes.`,
        acneImpactScore: 35,
      },
      {
        name: 'Tandoori Chicken',
        explanation: `Grilled chicken marinated in spices. No deep-frying, lower in oil and dairy.`,
        acneImpactScore: 38,
      },
      {
        name: 'Chana Masala',
        explanation: `Chickpea curry with tomatoes and spices. High in fiber, lower in dairy and oil.`,
        acneImpactScore: 40,
      },
      {
        name: 'Bhindi Masala',
        explanation: `Okra curry with minimal oil. High in fiber, no dairy, better for skin health.`,
        acneImpactScore: 32,
      },
      {
        name: 'Aloo Gobi',
        explanation: `Potato and cauliflower curry with minimal oil. High in fiber, lower in dairy.`,
        acneImpactScore: 38,
      },
      {
        name: 'Baingan Bharta',
        explanation: `Roasted eggplant curry. High in fiber, minimal oil, no dairy.`,
        acneImpactScore: 35,
      }
    );
  }
  
  // If it's biryani or heavy rice dish
  else if (lowerName.includes('biryani') || (lowerName.includes('rice') && tags.includes('deepFried'))) {
    alternatives.push(
      {
        name: 'Tandoori Chicken',
        explanation: `Grilled chicken marinated in spices. Much lower in oil than biryani, higher in protein.`,
        acneImpactScore: 38,
      },
      {
        name: 'Dal Tadka',
        explanation: `Lentil curry with minimal oil. High in protein and fiber, no deep-frying.`,
        acneImpactScore: 35,
      },
      {
        name: 'Chana Masala',
        explanation: `Chickpea curry with tomatoes and spices. High in fiber, lower glycemic than biryani.`,
        acneImpactScore: 40,
      },
      {
        name: 'Bhindi Masala',
        explanation: `Okra curry with minimal oil. High in fiber, no deep-frying.`,
        acneImpactScore: 32,
      },
      {
        name: 'Aloo Gobi',
        explanation: `Potato and cauliflower curry with minimal oil. High in fiber.`,
        acneImpactScore: 38,
      },
      {
        name: 'Baingan Bharta',
        explanation: `Roasted eggplant curry. High in fiber, minimal oil, no deep-frying.`,
        acneImpactScore: 35,
      }
    );
  }
  
  // If it's samosa, pakora, or other fried snacks
  else if (lowerName.includes('samosa') || lowerName.includes('pakora') || lowerName.includes('bhaji') || tags.includes('deepFried')) {
    alternatives.push(
      {
        name: 'Tandoori Chicken',
        explanation: `Grilled chicken instead of fried snacks. Much lower in oil, better for skin health.`,
        acneImpactScore: 38,
      },
      {
        name: 'Dal Tadka',
        explanation: `Lentil curry with minimal oil. High in protein and fiber, no deep-frying.`,
        acneImpactScore: 35,
      },
      {
        name: 'Chana Masala',
        explanation: `Chickpea curry. High in fiber, no deep-frying, better for acne health.`,
        acneImpactScore: 40,
      },
      {
        name: 'Bhindi Masala',
        explanation: `Okra curry with minimal oil. High in fiber, no deep-frying.`,
        acneImpactScore: 32,
      },
      {
        name: 'Aloo Gobi',
        explanation: `Potato and cauliflower curry with minimal oil. High in fiber and nutrients.`,
        acneImpactScore: 38,
      },
      {
        name: 'Baingan Bharta',
        explanation: `Roasted eggplant curry. High in fiber, minimal oil, no deep-frying.`,
        acneImpactScore: 35,
      }
    );
  }
  
  // Generic Indian alternatives if no specific match - expanded with more variety
  else {
    // Appetizers
    alternatives.push(
      {
        name: 'Tandoori Roti',
        explanation: `Whole wheat flatbread baked in tandoor. High in fiber, no deep-frying, better for skin health.`,
        acneImpactScore: 30,
      },
      {
        name: 'Cucumber Raita',
        explanation: `Cooling yogurt dip with cucumber. Rich in probiotics, hydrating, supports gut health.`,
        acneImpactScore: 35,
      },
      {
        name: 'Mixed Vegetable Salad',
        explanation: `Fresh mixed vegetables with light dressing. High in fiber and antioxidants, no oil.`,
        acneImpactScore: 25,
      },
      {
        name: 'Steamed Idli',
        explanation: `Steamed rice cakes. Low in oil, high in carbohydrates, gentle on skin.`,
        acneImpactScore: 32,
      }
    );
    
    // Curries (Main Dishes)
    alternatives.push(
      {
        name: 'Dal Tadka',
        explanation: `Lentil curry with minimal oil. High in protein and fiber, lower in dairy than creamy dishes.`,
        acneImpactScore: 35,
      },
      {
        name: 'Chana Masala',
        explanation: `Chickpea curry with tomatoes and spices. High in fiber, lower in dairy and oil.`,
        acneImpactScore: 40,
      },
      {
        name: 'Bhindi Masala',
        explanation: `Okra curry with minimal oil. High in fiber, no dairy, better for skin health.`,
        acneImpactScore: 32,
      },
      {
        name: 'Aloo Gobi',
        explanation: `Potato and cauliflower curry with minimal oil. High in fiber, lower in dairy.`,
        acneImpactScore: 38,
      },
      {
        name: 'Baingan Bharta',
        explanation: `Roasted eggplant curry. High in fiber, minimal oil, no dairy.`,
        acneImpactScore: 35,
      },
      {
        name: 'Rajma',
        explanation: `Kidney bean curry with tomatoes and spices. High in protein and fiber.`,
        acneImpactScore: 38,
      },
      {
        name: 'Sambar',
        explanation: `Lentil and vegetable stew. High in protein, fiber, and antioxidants.`,
        acneImpactScore: 33,
      },
      {
        name: 'Rasam',
        explanation: `Spiced tamarind soup. Light, anti-inflammatory, supports digestion and skin health.`,
        acneImpactScore: 28,
      },
      {
        name: 'Vegetable Korma (Light)',
        explanation: `Mixed vegetable curry with minimal cream. Request light cream for better skin health.`,
        acneImpactScore: 42,
      }
    );
    
    // Tandoor/Grilled Items
    alternatives.push(
      {
        name: 'Tandoori Chicken',
        explanation: `Grilled chicken marinated in spices. No deep-frying, lower in oil and dairy.`,
        acneImpactScore: 38,
      },
      {
        name: 'Tandoori Fish',
        explanation: `Grilled fish marinated in spices. High in omega-3s, no deep-frying, excellent for skin.`,
        acneImpactScore: 35,
      },
      {
        name: 'Tandoori Vegetables',
        explanation: `Grilled mixed vegetables. High in fiber and antioxidants, no oil, better for skin.`,
        acneImpactScore: 30,
      }
    );
    
    // Rice/Bread Options
    alternatives.push(
      {
        name: 'Steamed Basmati Rice',
        explanation: `Steamed basmati rice. Low in oil, high in carbohydrates, gentle on skin.`,
        acneImpactScore: 32,
      },
      {
        name: 'Brown Rice',
        explanation: `Whole grain brown rice. High in fiber, supports stable blood sugar and skin health.`,
        acneImpactScore: 30,
      }
    );
  }
  
  // Apply score-based filtering and shuffling to all cuisine-specific alternatives
  if (alternatives.length > 0) {
    // Score-based threshold: lower scores get stricter filtering
    // Increased thresholds to allow more options through
    const scoreThreshold = glowScore < 60 ? 42 : glowScore < 75 ? 45 : 48;
    const filteredAlternatives = alternatives.filter(item => item.acneImpactScore <= scoreThreshold);
    
    // Create seed for this function based on glowScore
    const localSeed = Math.floor(glowScore * 1000);
    
    // Shuffle based on user's skin health index for variety
    const shuffledAlternatives = seededShuffle(filteredAlternatives, localSeed + 6000);
    
    // Limit to 1-2 items if current item is already healthy, up to 6 for moderate/high
    const maxAlternatives = 6; // Only 'moderate' | 'high' at this point since 'low' was handled earlier
    return shuffledAlternatives.slice(0, maxAlternatives);
  }
  
  return [];
}

/**
 * Get Chinese restaurant specific menu items
 */
function getChineseRestaurantAlternatives(
  restaurantName: string,
  menuItemName: string,
  tags: string[],
  lowerName: string,
  acneImpactCategory: 'low' | 'moderate' | 'high',
  glowScore: number = 70
): RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] {
  const alternatives: RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] = [];
  
  if (tags.includes('deepFried') && (tags.includes('sweetSauce') || tags.includes('sugar'))) {
    alternatives.push(
      {
        name: 'Grilled Teriyaki Chicken with Steamed Vegetables',
        explanation: `Grilled chicken with teriyaki sauce, served with steamed vegetables. Much lower in oil and sugar than fried items.`,
        acneImpactScore: 42,
      },
      {
        name: 'Steamed Chicken with Mixed Vegetables',
        explanation: `Steamed chicken with vegetables. No oil, no deep-frying, excellent for skin health.`,
        acneImpactScore: 38,
      },
      {
        name: 'Mixed Vegetables with Steamed Rice',
        explanation: `Vegetable-based dish with steamed rice. Much lower in oil and sugar than fried items.`,
        acneImpactScore: 35,
      },
      {
        name: 'Steamed Fish with Vegetables',
        explanation: `Steamed fish with vegetables. High in omega-3s, no oil, better for skin health.`,
        acneImpactScore: 32,
      }
    );
  } else if (tags.includes('deepFried')) {
    alternatives.push(
      {
        name: 'Grilled Teriyaki Chicken',
        explanation: `Grilled chicken instead of fried. Much lower in oil, better for skin health.`,
        acneImpactScore: 40,
      },
      {
        name: 'Steamed Chicken with Broccoli',
        explanation: `Steamed chicken with vegetables. No oil, no deep-frying, excellent for acne health.`,
        acneImpactScore: 38,
      },
      {
        name: 'Mixed Vegetables with Steamed Rice',
        explanation: `Vegetable-based dish with steamed rice. Lower in oil and refined carbs.`,
        acneImpactScore: 35,
      }
    );
  } else {
    // Appetizers
    alternatives.push(
      {
        name: 'Steamed Dumplings',
        explanation: `Steamed vegetable or chicken dumplings. No deep-frying, lower in oil.`,
        acneImpactScore: 35,
      },
      {
        name: 'Miso Soup',
        explanation: `Light soup with probiotics. Supports gut health, no oil.`,
        acneImpactScore: 30,
      },
      {
        name: 'Seaweed Salad',
        explanation: `Fresh seaweed salad. Rich in iodine and antioxidants, low in calories.`,
        acneImpactScore: 28,
      },
      {
        name: 'Steamed Edamame',
        explanation: `Steamed soybeans. High in protein and fiber, no oil.`,
        acneImpactScore: 32,
      }
    );
    
    // Main Dishes
    alternatives.push(
      {
        name: 'Steamed Chicken with Mixed Vegetables',
        explanation: `Steamed preparation uses no oil, making it better for skin health.`,
        acneImpactScore: 38,
      },
      {
        name: 'Mixed Vegetables with Steamed Rice',
        explanation: `Vegetable-based dish with steamed rice. High in fiber, lower in oil.`,
        acneImpactScore: 35,
      },
      {
        name: 'Steamed Fish with Vegetables',
        explanation: `Steamed fish with vegetables. High in omega-3s, no oil, better for skin health.`,
        acneImpactScore: 32,
      },
      {
        name: 'Grilled Teriyaki Chicken',
        explanation: `Grilled chicken with teriyaki sauce. Lower in oil than fried items.`,
        acneImpactScore: 40,
      },
      {
        name: 'Steamed White Meat Chicken',
        explanation: `Steamed white meat chicken with vegetables. Lower in oil than fried items.`,
        acneImpactScore: 38,
      },
      {
        name: 'Steamed Chicken with Broccoli',
        explanation: `Steamed chicken with broccoli. No oil, no deep-frying, excellent for acne health.`,
        acneImpactScore: 38,
      },
      {
        name: 'Stir-Fried Vegetables (Light Oil)',
        explanation: `Stir-fried vegetables with minimal oil. Request light oil for better skin health.`,
        acneImpactScore: 42,
      },
      {
        name: 'Steamed Tofu with Vegetables',
        explanation: `Steamed tofu with mixed vegetables. High in protein, no oil, plant-based.`,
        acneImpactScore: 35,
      }
    );
  }
  
  // Apply score-based filtering and shuffling to all cuisine-specific alternatives
  if (alternatives.length > 0) {
    // Score-based threshold: lower scores get stricter filtering
    // Increased thresholds to allow more options through
    const scoreThreshold = glowScore < 60 ? 42 : glowScore < 75 ? 45 : 48;
    const filteredAlternatives = alternatives.filter(item => item.acneImpactScore <= scoreThreshold);
    
    // Create seed for this function based on glowScore
    const localSeed = Math.floor(glowScore * 1000);
    
    // Shuffle based on user's skin health index for variety
    const shuffledAlternatives = seededShuffle(filteredAlternatives, localSeed + 6000);
    
    // Limit to 1-2 items if current item is already healthy, up to 6 for moderate/high
    const maxAlternatives = 6; // Only 'moderate' | 'high' at this point since 'low' was handled earlier
    return shuffledAlternatives.slice(0, maxAlternatives);
  }
  
  return [];
}

/**
 * Get Mexican restaurant specific menu items
 */
function getMexicanRestaurantAlternatives(
  restaurantName: string,
  menuItemName: string,
  tags: string[],
  lowerName: string,
  acneImpactCategory: 'low' | 'moderate' | 'high',
  glowScore: number = 70
): RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] {
  const alternatives: RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] = [];
  
  if (tags.includes('deepFried') || tags.includes('dairy')) {
    alternatives.push(
      {
        name: 'Grilled Chicken Burrito Bowl',
        explanation: `Burrito bowl with grilled chicken, rice, beans, and vegetables. Skip cheese and sour cream. Much lower in dairy and processed fats.`,
        acneImpactScore: 40,
      },
      {
        name: 'Grilled Chicken Salad Bowl',
        explanation: `Salad bowl with grilled chicken, vegetables, and light dressing. Much lower in refined carbs and dairy.`,
        acneImpactScore: 35,
      },
      {
        name: 'Grilled Chicken Soft Tacos',
        explanation: `Soft tacos with grilled chicken and vegetables. Skip cheese and sour cream. Lower in dairy and oil.`,
        acneImpactScore: 42,
      },
      {
        name: 'Grilled Steak Bowl',
        explanation: `Bowl with grilled steak, rice, beans, and vegetables. Skip cheese and sour cream. Lower in dairy.`,
        acneImpactScore: 45,
      }
    );
  } else {
    // Appetizers
    alternatives.push(
      {
        name: 'Guacamole with Fresh Vegetables',
        explanation: `Fresh guacamole with vegetable sticks. High in healthy fats and fiber, no dairy.`,
        acneImpactScore: 32,
      },
      {
        name: 'Black Bean Soup',
        explanation: `Black bean soup. High in protein and fiber, supports gut health.`,
        acneImpactScore: 35,
      },
      {
        name: 'Grilled Vegetable Platter',
        explanation: `Grilled mixed vegetables. High in fiber and antioxidants, no oil.`,
        acneImpactScore: 30,
      },
      {
        name: 'Fresh Salsa with Baked Chips',
        explanation: `Fresh tomato salsa with baked chips. Request baked instead of fried chips.`,
        acneImpactScore: 38,
      }
    );
    
    // Main Dishes
    alternatives.push(
      {
        name: 'Grilled Chicken Burrito Bowl',
        explanation: `Burrito bowl with grilled protein and vegetables. Lower in refined carbs and dairy.`,
        acneImpactScore: 40,
      },
      {
        name: 'Grilled Chicken Salad Bowl',
        explanation: `Salad bowl with grilled protein. Much lower in refined carbs.`,
        acneImpactScore: 35,
      },
      {
        name: 'Grilled Chicken Soft Tacos',
        explanation: `Soft tacos with grilled chicken and vegetables. Skip cheese and sour cream.`,
        acneImpactScore: 42,
      },
      {
        name: 'Grilled Steak Bowl',
        explanation: `Bowl with grilled steak, rice, beans, and vegetables. Skip cheese and sour cream.`,
        acneImpactScore: 45,
      },
      {
        name: 'Black Bean Burrito Bowl',
        explanation: `Burrito bowl with black beans and vegetables. Skip cheese and sour cream. High in fiber.`,
        acneImpactScore: 40,
      },
      {
        name: 'Veggie Bowl',
        explanation: `Bowl with vegetables, rice, and beans. Skip cheese and sour cream. High in fiber.`,
        acneImpactScore: 35,
      },
      {
        name: 'Grilled Fish Tacos',
        explanation: `Soft tacos with grilled fish and vegetables. Skip cheese and sour cream. High in omega-3s.`,
        acneImpactScore: 38,
      },
      {
        name: 'Ceviche',
        explanation: `Fresh fish marinated in citrus. High in protein and omega-3s, no cooking oil.`,
        acneImpactScore: 30,
      }
    );
  }
  
  // Apply score-based filtering and shuffling to all cuisine-specific alternatives
  if (alternatives.length > 0) {
    // Score-based threshold: lower scores get stricter filtering
    // Increased thresholds to allow more options through
    const scoreThreshold = glowScore < 60 ? 42 : glowScore < 75 ? 45 : 48;
    const filteredAlternatives = alternatives.filter(item => item.acneImpactScore <= scoreThreshold);
    
    // Create seed for this function based on glowScore
    const localSeed = Math.floor(glowScore * 1000);
    
    // Shuffle based on user's skin health index for variety
    const shuffledAlternatives = seededShuffle(filteredAlternatives, localSeed + 6000);
    
    // Limit to 1-2 items if current item is already healthy, up to 6 for moderate/high
    const maxAlternatives = 6; // Only 'moderate' | 'high' at this point since 'low' was handled earlier
    return shuffledAlternatives.slice(0, maxAlternatives);
  }
  
  return [];
}

/**
 * Get Italian restaurant specific menu items
 */
function getItalianRestaurantAlternatives(
  restaurantName: string,
  menuItemName: string,
  tags: string[],
  lowerName: string,
  acneImpactCategory: 'low' | 'moderate' | 'high',
  glowScore: number = 70
): RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] {
  const alternatives: RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] = [];
  
  if (tags.includes('dairy') && (lowerName.includes('pasta') || lowerName.includes('alfredo') || lowerName.includes('carbonara'))) {
    alternatives.push(
      {
        name: 'Herb-grilled chicken breast',
        explanation: `Herb-grilled chicken breast with roasted vegetables. Much lower in dairy and refined carbs than creamy pasta.`,
        acneImpactScore: 38,
      },
      {
        name: 'Marinara Pasta with Grilled Chicken',
        explanation: `Tomato-based pasta with grilled chicken. Lower in dairy than creamy sauces.`,
        acneImpactScore: 45,
      },
      {
        name: 'Grilled Chicken Caesar Salad (Light Dressing)',
        explanation: `Caesar salad with grilled chicken and light dressing. Lower in dairy and refined carbs.`,
        acneImpactScore: 40,
      },
      {
        name: 'Pan-Seared Salmon',
        explanation: `Pan-seared salmon fillet with seasonal vegetables. High in omega-3s, no dairy, better for skin.`,
        acneImpactScore: 35,
      }
    );
  } else if (lowerName.includes('pizza')) {
    alternatives.push(
      {
        name: 'Herb-grilled chicken breast',
        explanation: `Herb-grilled chicken breast with roasted vegetables. Much lower in refined carbs and dairy than pizza.`,
        acneImpactScore: 38,
      },
      {
        name: 'Pan-Seared Salmon',
        explanation: `Pan-seared salmon fillet with seasonal vegetables. High in omega-3s, no refined carbs.`,
        acneImpactScore: 35,
      },
      {
        name: 'Caesar Salad with Grilled Chicken',
        explanation: `Salad with grilled chicken. Lower in refined carbs and dairy.`,
        acneImpactScore: 40,
      }
    );
  } else {
    // Appetizers
    alternatives.push(
      {
        name: 'Caprese Salad (Light Mozzarella)',
        explanation: `Fresh tomato and mozzarella salad. Request light mozzarella for lower dairy.`,
        acneImpactScore: 40,
      },
      {
        name: 'Grilled Vegetable Antipasto',
        explanation: `Grilled mixed vegetables. High in fiber and antioxidants, no dairy.`,
        acneImpactScore: 32,
      },
      {
        name: 'Minestrone Soup',
        explanation: `Vegetable soup. High in fiber and antioxidants, supports gut health.`,
        acneImpactScore: 35,
      },
      {
        name: 'Arugula Salad',
        explanation: `Fresh arugula salad with light dressing. High in antioxidants, no dairy.`,
        acneImpactScore: 30,
      }
    );
    
    // Main Dishes
    alternatives.push(
      {
        name: 'Herb-grilled chicken breast',
        explanation: `Grilled chicken with roasted vegetables. Lower in dairy and refined carbs.`,
        acneImpactScore: 38,
      },
      {
        name: 'Pan-Seared Salmon',
        explanation: `Grilled salmon with vegetables. High in omega-3s, better for skin.`,
        acneImpactScore: 35,
      },
      {
        name: 'Marinara Pasta with Grilled Chicken',
        explanation: `Tomato-based pasta with grilled chicken. Lower in dairy than creamy sauces.`,
        acneImpactScore: 42,
      },
      {
        name: 'Grilled Chicken Caesar Salad (Light Dressing)',
        explanation: `Caesar salad with grilled chicken and light dressing. Lower in dairy and refined carbs.`,
        acneImpactScore: 40,
      },
      {
        name: 'Grilled Vegetable Platter',
        explanation: `Grilled mixed vegetables. High in fiber and antioxidants, no dairy.`,
        acneImpactScore: 32,
      },
      {
        name: 'Bruschetta (Light Cheese)',
        explanation: `Toasted bread with tomatoes and basil. Request light cheese for lower dairy.`,
        acneImpactScore: 38,
      }
    );
  }
  
  // Apply score-based filtering and shuffling to all cuisine-specific alternatives
  if (alternatives.length > 0) {
    // Score-based threshold: lower scores get stricter filtering
    // Increased thresholds to allow more options through
    const scoreThreshold = glowScore < 60 ? 42 : glowScore < 75 ? 45 : 48;
    const filteredAlternatives = alternatives.filter(item => item.acneImpactScore <= scoreThreshold);
    
    // Create seed for this function based on glowScore
    const localSeed = Math.floor(glowScore * 1000);
    
    // Shuffle based on user's skin health index for variety
    const shuffledAlternatives = seededShuffle(filteredAlternatives, localSeed + 6000);
    
    // Limit to 1-2 items if current item is already healthy, up to 6 for moderate/high
    const maxAlternatives = 6; // Only 'moderate' | 'high' at this point since 'low' was handled earlier
    return shuffledAlternatives.slice(0, maxAlternatives);
  }
  
  return [];
}

/**
 * Get Japanese restaurant specific menu items
 */
function getJapaneseRestaurantAlternatives(
  restaurantName: string,
  menuItemName: string,
  tags: string[],
  lowerName: string,
  acneImpactCategory: 'low' | 'moderate' | 'high',
  glowScore: number = 70
): RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] {
  const alternatives: RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] = [];
  
  if (tags.includes('deepFried')) {
    alternatives.push(
      {
        name: 'Grilled Teriyaki Chicken with Steamed Vegetables',
        explanation: `Grilled chicken with teriyaki sauce and steamed vegetables. Much lower in oil than fried items.`,
        acneImpactScore: 40,
      },
      {
        name: 'Salmon Teriyaki with Steamed Rice',
        explanation: `Grilled salmon with teriyaki sauce and steamed rice. High in omega-3s, lower in oil.`,
        acneImpactScore: 35,
      },
      {
        name: 'Chicken Teriyaki Bowl',
        explanation: `Grilled chicken teriyaki bowl with vegetables and rice. Lower in oil than fried items.`,
        acneImpactScore: 42,
      },
      {
        name: 'Sashimi Platter',
        explanation: `Fresh raw fish without rice or frying. High in omega-3s, no oil, excellent for skin.`,
        acneImpactScore: 25,
      }
    );
  } else {
    // Appetizers
    alternatives.push(
      {
        name: 'Miso Soup',
        explanation: `Light soup with probiotics. Supports gut health, no oil.`,
        acneImpactScore: 30,
      },
      {
        name: 'Seaweed Salad',
        explanation: `Fresh seaweed salad. Rich in iodine and antioxidants, low in calories.`,
        acneImpactScore: 28,
      },
      {
        name: 'Steamed Edamame',
        explanation: `Steamed soybeans. High in protein and fiber, no oil.`,
        acneImpactScore: 32,
      },
      {
        name: 'Sashimi Platter',
        explanation: `Fresh raw fish. High in omega-3s, no oil, excellent for skin.`,
        acneImpactScore: 25,
      },
      {
        name: 'Steamed Dumplings',
        explanation: `Steamed vegetable or chicken dumplings. No deep-frying, lower in oil.`,
        acneImpactScore: 35,
      }
    );
    
    // Main Dishes
    alternatives.push(
      {
        name: 'Salmon Teriyaki with Steamed Vegetables',
        explanation: `Grilled salmon with vegetables. High in omega-3s, better for skin health.`,
        acneImpactScore: 35,
      },
      {
        name: 'Chicken Teriyaki Bowl',
        explanation: `Grilled chicken teriyaki with vegetables and rice. Lower in oil.`,
        acneImpactScore: 40,
      },
      {
        name: 'Grilled Teriyaki Chicken',
        explanation: `Grilled chicken with teriyaki sauce. Lower in oil than fried items.`,
        acneImpactScore: 40,
      },
      {
        name: 'Steamed Chicken with Broccoli',
        explanation: `Steamed chicken with vegetables. No oil, no deep-frying, excellent for acne health.`,
        acneImpactScore: 38,
      },
      {
        name: 'Mixed Vegetables with Steamed Rice',
        explanation: `Vegetable-based dish with steamed rice. High in fiber, lower in oil.`,
        acneImpactScore: 35,
      },
      {
        name: 'Steamed Fish with Vegetables',
        explanation: `Steamed fish with vegetables. High in omega-3s, no oil, better for skin health.`,
        acneImpactScore: 32,
      },
      {
        name: 'Steamed Tofu with Vegetables',
        explanation: `Steamed tofu with mixed vegetables. High in protein, no oil, plant-based.`,
        acneImpactScore: 35,
      },
      {
        name: 'Chirashi Bowl',
        explanation: `Sashimi over rice. High in omega-3s, no cooking oil, excellent for skin.`,
        acneImpactScore: 30,
      }
    );
  }
  
  // Apply score-based filtering and shuffling to all cuisine-specific alternatives
  if (alternatives.length > 0) {
    // Score-based threshold: lower scores get stricter filtering
    // Increased thresholds to allow more options through
    const scoreThreshold = glowScore < 60 ? 42 : glowScore < 75 ? 45 : 48;
    const filteredAlternatives = alternatives.filter(item => item.acneImpactScore <= scoreThreshold);
    
    // Create seed for this function based on glowScore
    const localSeed = Math.floor(glowScore * 1000);
    
    // Shuffle based on user's skin health index for variety
    const shuffledAlternatives = seededShuffle(filteredAlternatives, localSeed + 6000);
    
    // Limit to 1-2 items if current item is already healthy, up to 6 for moderate/high
    const maxAlternatives = 6; // Only 'moderate' | 'high' at this point since 'low' was handled earlier
    return shuffledAlternatives.slice(0, maxAlternatives);
  }
  
  return [];
}

/**
 * Get Thai restaurant specific menu items
 */
function getThaiRestaurantAlternatives(
  restaurantName: string,
  menuItemName: string,
  tags: string[],
  lowerName: string,
  acneImpactCategory: 'low' | 'moderate' | 'high',
  glowScore: number = 70
): RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] {
  const alternatives: RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] = [];
  
  if (tags.includes('deepFried')) {
    alternatives.push(
      {
        name: 'Herb-grilled chicken breast',
        explanation: `Grilled chicken with steamed vegetables. Much lower in oil than fried items.`,
        acneImpactScore: 38,
      },
      {
        name: 'Tom Yum Soup with Grilled Chicken',
        explanation: `Spicy soup with grilled chicken. Lower in oil, high in vegetables.`,
        acneImpactScore: 35,
      },
      {
        name: 'Pad Thai with Grilled Chicken (Less Oil)',
        explanation: `Stir-fried noodles with grilled chicken, request less oil. Lower in oil than fried items.`,
        acneImpactScore: 42,
      },
      {
        name: 'Green Curry with Steamed Rice (Vegetables)',
        explanation: `Vegetable green curry with steamed rice. Lower in oil, high in vegetables.`,
        acneImpactScore: 40,
      }
    );
  } else {
    // Appetizers
    alternatives.push(
      {
        name: 'Tom Yum Soup',
        explanation: `Spicy and sour soup. Light, anti-inflammatory, supports digestion.`,
        acneImpactScore: 32,
      },
      {
        name: 'Papaya Salad (Som Tam)',
        explanation: `Fresh papaya salad. High in fiber and vitamin C, no oil.`,
        acneImpactScore: 30,
      },
      {
        name: 'Steamed Vegetable Spring Rolls',
        explanation: `Steamed vegetable spring rolls. No deep-frying, lower in oil.`,
        acneImpactScore: 35,
      },
      {
        name: 'Fresh Spring Rolls',
        explanation: `Fresh vegetable spring rolls with herbs. No cooking, high in fiber.`,
        acneImpactScore: 28,
      }
    );
    
    // Main Dishes
    alternatives.push(
      {
        name: 'Tom Yum Soup with Grilled Chicken',
        explanation: `Spicy soup with grilled chicken. Lower in oil, high in vegetables.`,
        acneImpactScore: 35,
      },
      {
        name: 'Green Curry with Steamed Rice',
        explanation: `Vegetable curry with steamed rice. Lower in oil.`,
        acneImpactScore: 40,
      },
      {
        name: 'Herb-grilled chicken breast',
        explanation: `Grilled chicken with steamed vegetables. Much lower in oil than fried items.`,
        acneImpactScore: 38,
      },
      {
        name: 'Pad Thai with Grilled Chicken (Less Oil)',
        explanation: `Stir-fried noodles with grilled chicken, request less oil. Lower in oil than fried items.`,
        acneImpactScore: 42,
      },
      {
        name: 'Steamed Fish with Vegetables',
        explanation: `Steamed fish with vegetables. High in omega-3s, no oil, better for skin health.`,
        acneImpactScore: 32,
      },
      {
        name: 'Pad See Ew with Grilled Chicken (Less Oil)',
        explanation: `Stir-fried wide noodles with grilled chicken, request less oil. Lower in oil.`,
        acneImpactScore: 40,
      },
      {
        name: 'Steamed Vegetables with Jasmine Rice',
        explanation: `Steamed mixed vegetables with jasmine rice. High in fiber, no oil.`,
        acneImpactScore: 35,
      },
      {
        name: 'Larb (Chicken or Tofu)',
        explanation: `Spiced meat or tofu salad. High in protein, no oil, supports digestion.`,
        acneImpactScore: 38,
      }
    );
  }
  
  // Apply score-based filtering and shuffling to all cuisine-specific alternatives
  if (alternatives.length > 0) {
    // Score-based threshold: lower scores get stricter filtering
    // Increased thresholds to allow more options through
    const scoreThreshold = glowScore < 60 ? 42 : glowScore < 75 ? 45 : 48;
    const filteredAlternatives = alternatives.filter(item => item.acneImpactScore <= scoreThreshold);
    
    // Create seed for this function based on glowScore
    const localSeed = Math.floor(glowScore * 1000);
    
    // Shuffle based on user's skin health index for variety
    const shuffledAlternatives = seededShuffle(filteredAlternatives, localSeed + 6000);
    
    // Limit to 1-2 items if current item is already healthy, up to 6 for moderate/high
    const maxAlternatives = 6; // Only 'moderate' | 'high' at this point since 'low' was handled earlier
    return shuffledAlternatives.slice(0, maxAlternatives);
  }
  
  return [];
}

/**
 * Get fast food restaurant specific menu items
 */
function getFastFoodAlternatives(
  restaurantName: string,
  menuItemName: string,
  tags: string[],
  lowerName: string,
  acneImpactCategory: 'low' | 'moderate' | 'high',
  glowScore: number = 70
): RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] {
  const alternatives: RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] = [];
  
  if (tags.includes('deepFried') || lowerName.includes('burger') || lowerName.includes('fries')) {
    alternatives.push(
      {
        name: 'Grilled Chicken Sandwich',
        explanation: `Grilled chicken sandwich instead of burger. Much lower in processed fats.`,
        acneImpactScore: 42,
      },
      {
        name: 'Grilled Chicken Salad',
        explanation: `Salad with grilled chicken. Much lower in refined carbs and processed fats.`,
        acneImpactScore: 35,
      },
      {
        name: 'Grilled Chicken Wrap',
        explanation: `Wrap with grilled chicken and vegetables. Lower in processed fats than burgers.`,
        acneImpactScore: 40,
      },
      {
        name: 'Grilled Chicken Bowl',
        explanation: `Bowl with grilled chicken and vegetables. Lower in refined carbs and processed fats.`,
        acneImpactScore: 38,
      }
    );
  } else {
    alternatives.push(
      {
        name: 'Grilled Chicken Sandwich',
        explanation: `Grilled chicken sandwich. Lower in processed fats.`,
        acneImpactScore: 42,
      },
      {
        name: 'Grilled Chicken Salad',
        explanation: `Salad with grilled chicken. Lower in refined carbs.`,
        acneImpactScore: 35,
      },
      {
        name: 'Grilled Chicken Wrap',
        explanation: `Wrap with grilled chicken and vegetables. Lower in processed fats than burgers.`,
        acneImpactScore: 40,
      },
      {
        name: 'Grilled Chicken Bowl',
        explanation: `Bowl with grilled chicken and vegetables. Lower in refined carbs and processed fats.`,
        acneImpactScore: 38,
      },
      {
        name: 'Grilled Chicken Caesar Salad',
        explanation: `Caesar salad with grilled chicken. Lower in refined carbs than burgers and fries.`,
        acneImpactScore: 38,
      },
      {
        name: 'Side Garden Salad',
        explanation: `Fresh garden salad. High in fiber, no processed ingredients.`,
        acneImpactScore: 30,
      }
    );
  }
  
  // Apply score-based filtering and shuffling to all cuisine-specific alternatives
  if (alternatives.length > 0) {
    // Score-based threshold: lower scores get stricter filtering
    // Increased thresholds to allow more options through
    const scoreThreshold = glowScore < 60 ? 42 : glowScore < 75 ? 45 : 48;
    const filteredAlternatives = alternatives.filter(item => item.acneImpactScore <= scoreThreshold);
    
    // Create seed for this function based on glowScore
    const localSeed = Math.floor(glowScore * 1000);
    
    // Shuffle based on user's skin health index for variety
    const shuffledAlternatives = seededShuffle(filteredAlternatives, localSeed + 6000);
    
    // Limit to 1-2 items if current item is already healthy, up to 6 for moderate/high
    const maxAlternatives = 6; // Only 'moderate' | 'high' at this point since 'low' was handled earlier
    return shuffledAlternatives.slice(0, maxAlternatives);
  }
  
  return [];
}

/**
 * Get American casual restaurant specific menu items
 */
function getAmericanCasualAlternatives(
  restaurantName: string,
  menuItemName: string,
  tags: string[],
  lowerName: string,
  acneImpactCategory: 'low' | 'moderate' | 'high',
  glowScore: number = 70
): RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] {
  const alternatives: RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] = [];
  
  if (tags.includes('deepFried')) {
    alternatives.push(
      {
        name: 'Pan-Seared Salmon',
        explanation: `Fresh salmon fillet pan-seared to perfection, served with seasonal vegetables and a light citrus-herb sauce. Much lower in oil than fried items.`,
        acneImpactScore: 35,
      },
      {
        name: 'Herb-grilled chicken breast',
        explanation: `Herb-grilled chicken breast with garlic mashed potatoes and roasted vegetables. Much lower in oil than fried items.`,
        acneImpactScore: 38,
      },
      {
        name: 'Grilled Chicken Caesar Salad',
        explanation: `Caesar salad with grilled chicken. Much lower in refined carbs and oil than fried items.`,
        acneImpactScore: 35,
      },
      {
        name: 'Herb-grilled chicken breast',
        explanation: `Herb-grilled chicken breast with roasted vegetables and quinoa. Lower in oil than fried items.`,
        acneImpactScore: 40,
      },
      {
        name: 'Pan-Seared White Fish',
        explanation: `Pan-seared white fish with seasonal vegetables and lemon butter sauce. Lower in oil than fried items.`,
        acneImpactScore: 35,
      },
      {
        name: 'Garden Fresh Salad with Grilled Chicken',
        explanation: `Fresh garden salad with grilled chicken, seasonal vegetables, and light dressing. Much lower in refined carbs and oil.`,
        acneImpactScore: 35,
      }
    );
  } else {
    alternatives.push(
      {
        name: 'Pan-Seared Salmon',
        explanation: `Fresh salmon fillet pan-seared to perfection, served with seasonal vegetables and a light citrus-herb sauce. High in omega-3s, better for skin health.`,
        acneImpactScore: 35,
      },
      {
        name: 'Herb-grilled chicken breast',
        explanation: `Herb-grilled chicken breast with garlic mashed potatoes and roasted vegetables. Lower in oil and refined carbs.`,
        acneImpactScore: 38,
      },
      {
        name: 'Grilled Chicken Caesar Salad',
        explanation: `Caesar salad with grilled chicken. Much lower in refined carbs and oil.`,
        acneImpactScore: 35,
      },
      {
        name: 'Herb-grilled chicken breast',
        explanation: `Herb-grilled chicken breast with roasted vegetables and quinoa. Lower in oil than fried items.`,
        acneImpactScore: 40,
      },
      {
        name: 'Pan-Seared White Fish',
        explanation: `Pan-seared white fish with seasonal vegetables and lemon butter sauce. High in protein, lower in oil.`,
        acneImpactScore: 35,
      },
      {
        name: 'Garden Fresh Salad with Grilled Chicken',
        explanation: `Fresh garden salad with grilled chicken, seasonal vegetables, and light dressing. High in fiber, lower in refined carbs.`,
        acneImpactScore: 35,
      }
    );
  }
  
  // Apply score-based filtering and shuffling to all cuisine-specific alternatives
  if (alternatives.length > 0) {
    // Score-based threshold: lower scores get stricter filtering
    // Increased thresholds to allow more options through
    const scoreThreshold = glowScore < 60 ? 42 : glowScore < 75 ? 45 : 48;
    const filteredAlternatives = alternatives.filter(item => item.acneImpactScore <= scoreThreshold);
    
    // Create seed for this function based on glowScore
    const localSeed = Math.floor(glowScore * 1000);
    
    // Shuffle based on user's skin health index for variety
    const shuffledAlternatives = seededShuffle(filteredAlternatives, localSeed + 6000);
    
    // Limit to 1-2 items if current item is already healthy, up to 6 for moderate/high
    const maxAlternatives = 6; // Only 'moderate' | 'high' at this point since 'low' was handled earlier
    return shuffledAlternatives.slice(0, maxAlternatives);
  }
  
  return [];
}

/**
 * Get upgrades for already low-impact items
 */
function getLowImpactUpgrades(
  restaurantCuisine: string,
  restaurantName: string,
  menuItemName: string
): RestaurantMenuAnalysisResult['sameRestaurantAlternatives'] {
  if (restaurantCuisine === 'indian') {
    return [
      {
        name: 'Dal Tadka with Brown Rice',
        explanation: `A lighter lentil curry with brown rice. Even more fiber and lower glycemic impact.`,
        acneImpactScore: 30,
      },
      {
        name: 'Tandoori Chicken with Steamed Vegetables',
        explanation: `Grilled chicken with steamed vegetables. High protein, no oil.`,
        acneImpactScore: 28,
      }
    ];
  } else if (restaurantCuisine === 'chinese' || restaurantCuisine === 'chinese_american') {
    return [
      {
        name: 'Steamed Chicken with Mixed Vegetables',
        explanation: `Steamed preparation uses no oil, making it even better for skin health.`,
        acneImpactScore: 30,
      },
      {
        name: 'Mixed Vegetables with Steamed Rice',
        explanation: `Vegetable-based dish with steamed rice. High in fiber, no oil.`,
        acneImpactScore: 28,
      }
    ];
  } else {
    return [
      {
        name: 'Grilled Chicken Breast with Extra Vegetables',
        explanation: `Grilled chicken breast with extra vegetables to increase fiber and nutrients.`,
        acneImpactScore: 25,
      },
      {
        name: 'Steamed Chicken Breast',
        explanation: `If available, choose steamed chicken breast instead of grilled for even less oil.`,
        acneImpactScore: 22,
      }
    ];
  }
}

